import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Avatar, Box, Button, Chip, FormControl, IconButton, LinearProgress, MenuItem, Pagination, Paper, Select, SelectChangeEvent, Snackbar, SnackbarCloseReason, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Typography, useMediaQuery, useTheme, alpha } from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  LinhasEsqueletoTabela,
  ListagemBuscaField,
  OrdenarPorControl,
  codigoInternoSx,
  paperListagemSx,
  tableContainerListagemSx,
  valorMonetarioSx,
} from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce, useListagemAltura, useListagemKeyboardNav } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { IListagemDeProdutos, ProdutosService } from '../../../shared/services/api/estoque/produtos/ProdutosService';
import { brand } from '../../../shared/themes';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import { CelulaListaMobile, LinhaListagemMobile, ListaMobile, useCelular, moedaMobile } from '../../../shared/components/listagem-mobile';

const OPCOES_POR_PAGINA = [10, 25, 50, 100];

export const ListagemDeProdutos: React.FC = () => {
  const listaCelular = useCelular();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestIdRef = useRef(0);
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  // No celular esconde Cód./Un/Marca/Referência/Tipo/Local (secundárias) em vez de
  // deixar a tabela rolar na horizontal — cabe tudo na tela só com Produto/Qtd/Valor/Ação.
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const [rows, setRows] = useState<IListagemDeProdutos[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openMsg, setOpenMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const ROWS_CACHE_KEY = 'produtos';
  // Altura fixa de uma linha de produto: avatar (32px) + padding vertical (py: 1.25 => 20px) +
  // borda. Todas as colunas usam noWrap (uma linha só, incluindo a descrição — sem EAN
  // embaixo), então a altura real nunca varia com o conteúdo — por isso pode ser uma
  // constante em vez de medida no DOM. Medir uma linha real exigiria que os produtos já
  // tivessem chegado da API, obrigando uma primeira requisição "de calibração" com um limite
  // ainda errado e uma segunda já corrigida.
  const { tabelaRef, containerHeight, rowsPerPage, setRowsPerPage } = useListagemAltura({
    cacheKey: ROWS_CACHE_KEY,
    valorInicial: Environment.LIMITE_DE_LINHASSMALL,
    rowHeight: 56,
  });

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
  };

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenMsg(false);
  };

  const tipo = useMemo(() => {
    return searchParams.get('tipo') || 'descricao';
  }, [searchParams]);

  const filter = useMemo(() => {
    return searchParams.get('filter') || '';
  }, [searchParams]);

  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);

  // 'inicio' por padrão — busca só pelo começo do texto, mais rápida e mais
  // previsível pro operador digitando. 'contem' (em qualquer parte do
  // texto) é a alternativa selecionável no seletor "Modo de busca".
  const modobusca = useMemo(() => {
    return searchParams.get('modobusca') || 'inicio';
  }, [searchParams]);

  // Identifica sob que busca uma página foi carregada — um cache de página
  // adjacente só é válido se filtro/tipo/modo/tamanho de página não
  // mudaram desde que ele foi buscado.
  const contextoBusca = useMemo(
    () => `${tipo}|${filter}|${modobusca}|${rowsPerPage}`,
    [tipo, filter, modobusca, rowsPerPage],
  );

  // Página anterior/seguinte pré-carregadas em segundo plano (ver efeito de
  // prefetch abaixo) — permite a seta pra cima/baixo "rolar" através do
  // limite da página sem o flash de carregando de uma busca nova.
  const cacheAdjacenteRef = useRef<{
    anterior: { pagina: number; contexto: string; data: IListagemDeProdutos[]; totalCount: number } | null;
    seguinte: { pagina: number; contexto: string; data: IListagemDeProdutos[]; totalCount: number } | null;
  }>({ anterior: null, seguinte: null });

  useEffect(() => {
    // Página adjacente já pré-carregada (mesmo contexto de busca) — usa
    // direto do cache, sem loading nem ida ao servidor.
    const cacheAtual = cacheAdjacenteRef.current;
    const cache = cacheAtual.anterior?.pagina === pagina && cacheAtual.anterior?.contexto === contextoBusca
      ? cacheAtual.anterior
      : cacheAtual.seguinte?.pagina === pagina && cacheAtual.seguinte?.contexto === contextoBusca
        ? cacheAtual.seguinte
        : null;
    if (cache) {
      cacheAdjacenteRef.current = { anterior: null, seguinte: null };
      ++requestIdRef.current;
      setIsLoading(false);
      setRows(cache.data);
      setTotalCount(cache.totalCount);
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    debounce(() => {
      // Só zera a listagem quando a busca "debounced" realmente vai disparar (não a cada
      // tecla digitada), e sempre imediatamente antes do fetch — assim nunca fica visível
      // uma mistura de itens da pesquisa anterior com o resultado da pesquisa atual: ou
      // mostra o resultado antigo completo (enquanto o usuário ainda digita) ou mostra
      // "carregando" e depois só o resultado mais recente.
      if (requestId !== requestIdRef.current) return;
      setRows([]);
      setTotalCount(0);

      ProdutosService.getAll(pagina, filter, tipo, rowsPerPage, modobusca)
        .then((result) => {
          if (requestId !== requestIdRef.current) return;
          setIsLoading(false);
          if (result instanceof Error) {
            setRows([]);
            setTotalCount(0);
            setMsgType('error');
            setMsg(result.message);
            setOpenMsg(true);
            return;
          }
          if (!result.data || result.data.length === 0) {
            setRows([]);
            setTotalCount(0);
            return;
          }
          setRows(result.data);
          setTotalCount(result.totalCount);
        });
    });
  }, [filter, pagina, tipo, modobusca, debounce, rowsPerPage, contextoBusca]);

  // Pré-carrega a página anterior e a seguinte assim que a atual termina de
  // carregar — silencioso (não mexe em isLoading/rows), só alimenta o
  // cache acima.
  useEffect(() => {
    if (isLoading || rows.length === 0) return;
    const totalPaginasAtual = Math.max(1, Math.ceil(totalCount / rowsPerPage));
    const proximaPagina = pagina + 1 <= totalPaginasAtual ? pagina + 1 : null;
    const paginaAnterior = pagina > 1 ? pagina - 1 : null;
    const cacheAtual = cacheAdjacenteRef.current;
    let cancelado = false;

    if (proximaPagina !== null && !(cacheAtual.seguinte?.pagina === proximaPagina && cacheAtual.seguinte?.contexto === contextoBusca)) {
      ProdutosService.getAll(proximaPagina, filter, tipo, rowsPerPage, modobusca)
        .then((result) => {
          if (cancelado || result instanceof Error || !result.data || result.data.length === 0) return;
          cacheAdjacenteRef.current = {
            ...cacheAdjacenteRef.current,
            seguinte: { pagina: proximaPagina, contexto: contextoBusca, data: result.data, totalCount: result.totalCount },
          };
        });
    }
    if (paginaAnterior !== null && !(cacheAtual.anterior?.pagina === paginaAnterior && cacheAtual.anterior?.contexto === contextoBusca)) {
      ProdutosService.getAll(paginaAnterior, filter, tipo, rowsPerPage, modobusca)
        .then((result) => {
          if (cancelado || result instanceof Error || !result.data || result.data.length === 0) return;
          cacheAdjacenteRef.current = {
            ...cacheAdjacenteRef.current,
            anterior: { pagina: paginaAnterior, contexto: contextoBusca, data: result.data, totalCount: result.totalCount },
          };
        });
    }

    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina, totalCount, isLoading, contextoBusca]);

  useEffect(() => {
    if (pagina !== 1) {
      setSearchParams({ filter, tipo, modobusca, pagina: '1' }, { replace: true });
    }
  }, [rowsPerPage]);

  const dadosSelecao = [
    { nameBD: 'descricao', descricao: 'Descrição (F8)', placeholder: 'Pesquisar por descrição do produto...' },
    { nameBD: 'referencia', descricao: 'Referência (F8)', placeholder: 'Pesquisar por referência...' },
    { nameBD: 'codinterno', descricao: 'Código Interno (F8)', placeholder: 'Pesquisar por código interno...' },
    { nameBD: 'codbarra', descricao: 'Código de Ean (F8)', placeholder: 'Pesquisar por código de EAN...' },
  ];
  const modosDeBusca = [
    { nameBD: 'inicio', descricao: 'Início do texto' },
    { nameBD: 'contem', descricao: 'Em qualquer parte do texto' },
  ];
  // Explica o campo mesmo vazio, e muda junto com o tipo de busca selecionado
  // (Ordenar por/F8) — sem isso, o texto genérico não deixava claro por qual
  // campo a pesquisa ia filtrar de fato.
  const placeholderPesquisa = dadosSelecao.find(item => item.nameBD === tipo)?.placeholder ?? 'Pesquisar produtos...';

  const primeiroItem = totalCount === 0 ? 0 : (pagina - 1) * rowsPerPage + 1;
  const ultimoItem = Math.min(pagina * rowsPerPage, totalCount);
  const totalPaginas = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  const { pesquisaRef, linhaRefCallback, onBuscaKeyDown, onLinhaKeyDown } = useListagemKeyboardNav({
    rowsLength: rows.length,
    pagina,
    totalPaginas,
    onIrParaPagina: (novaPagina) => setSearchParams({ filter, tipo, modobusca, pagina: novaPagina.toString() }, { replace: true }),
    onEnter: (index) => navigate(`/produto/${rows[index].idproduto}`, { state: { from: `${location.pathname}${location.search}` } }),
    tiposBusca: dadosSelecao.map((item) => item.nameBD),
    tipoAtual: tipo,
    onTrocarTipo: (novoTipo) => setSearchParams({ filter, tipo: novoTipo, modobusca, pagina: '1' }, { replace: true }),
    rowsDep: [rows],
  });

  return (
    <>
      <LayoutBaseDePagina
        titulo='Produtos'
        subtitulo='Visualize, pesquise e gerencie todos os produtos cadastrados no sistema.'
        acao={
          <Button
            variant='contained'
            disableElevation
            startIcon={<AddCircleOutlineRoundedIcon />}
            onClick={() => navigate('/produto/novo', { state: { from: `${location.pathname}${location.search}` } })}
            sx={{
              height: 44,
              borderRadius: '10px',
              px: 2.5,
              fontWeight: 600,
              // Navy fixo da marca (não a cor de acento/ciano, e não
              // `theme.torq.sidebar.bg`) — é o tom usado no botão "Novo X" da
              // referência visual. Precisa ser um valor fixo, não o token da
              // sidebar: no tema "Claro (menu claro)" a sidebar é branca, e
              // `theme.torq.sidebar.bg` virava branco também — texto branco
              // em cima de fundo branco, botão invisível.
              bgcolor: brand.menuBase,
              color: '#fff',
              '&:hover': { bgcolor: '#012D3D' },
            }}
          >
            Novo Produto
          </Button>
        }
        // mx:1 (não 3) — precisa bater exatamente com a margem lateral que
        // LayoutBaseDePagina aplica em torno do Paper da listagem (ver
        // `Box marginX={1}` em torno de `children`), senão a caixa "Ordenar
        // por" e o fim do campo de busca ficam desalinhados da borda da
        // listagem embaixo.
        barraDeFerramentas={
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mx: 1, mb: 2.5 }}>
            {smDown ? (
              // No celular o campo de busca fica só (linha cheia) e os dois seletores
              // ("Ordenar por"/"Modo de busca") ficam lado a lado numa linha própria —
              // em vez de cada um empilhado na sua própria linha inteira, como o
              // flex-wrap fazia por padrão (cada um pedindo largura demais pra caber
              // junto de outro).
              <>
                <ListagemBuscaField
                  value={filter}
                  onChange={(valor) => setSearchParams({ filter: valor, tipo, modobusca, pagina: '1' }, { replace: true })}
                  placeholder={placeholderPesquisa}
                  inputRef={pesquisaRef}
                  onKeyDown={onBuscaKeyDown}
                />
                <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
                  <OrdenarPorControl
                    opcoes={dadosSelecao}
                    valorSelecionado={tipo}
                    onChange={(novoTipo) => setSearchParams({ filter, tipo: novoTipo, modobusca, pagina: '1' }, { replace: true })}
                  />
                  <OrdenarPorControl
                    label='Modo de busca'
                    opcoes={modosDeBusca}
                    valorSelecionado={modobusca}
                    onChange={(novoModo) => setSearchParams({ filter, tipo, modobusca: novoModo, pagina: '1' }, { replace: true })}
                  />
                </Box>
              </>
            ) : (
              <>
                <OrdenarPorControl
                  opcoes={dadosSelecao}
                  valorSelecionado={tipo}
                  onChange={(novoTipo) => setSearchParams({ filter, tipo: novoTipo, modobusca, pagina: '1' }, { replace: true })}
                />

                <ListagemBuscaField
                  value={filter}
                  onChange={(valor) => setSearchParams({ filter: valor, tipo, modobusca, pagina: '1' }, { replace: true })}
                  placeholder={placeholderPesquisa}
                  inputRef={pesquisaRef}
                  onKeyDown={onBuscaKeyDown}
                />

                {/* Vale pros 4 tipos de busca (Descrição/Referência/Código Interno/EAN) —
                    todos usam o mesmo padrão início/contém no backend agora. */}
                <OrdenarPorControl
                  label='Modo de busca'
                  opcoes={modosDeBusca}
                  valorSelecionado={modobusca}
                  onChange={(novoModo) => setSearchParams({ filter, tipo, modobusca: novoModo, pagina: '1' }, { replace: true })}
                />
              </>
            )}
          </Box>
        }
      >
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={openMsg}
          autoHideDuration={msgType === 'error' ? null : 6000}
          onClose={handleCloseMsg}
        >
          <Alert
            onClose={handleCloseMsg}
            severity={msgType}
            variant='filled'
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>
        <Paper
          elevation={0}
          sx={paperListagemSx(theme)}
        >
          <TableContainer
            ref={tabelaRef}
            // 'auto' (não altura cheia) quando a página atual não preenche rowsPerPage (única
            // página curta ou última página parcial de uma listagem paginada), para o fundo
            // acompanhar a quantidade real de itens em vez de esticar até o fim da tela. Durante
            // o carregamento a lista é zerada (ver comentário no efeito de busca) — sem manter a
            // altura cheia aqui, o container encolhe pro tamanho só do cabeçalho e o cartão de
            // loading fica espremido lá em cima em vez de centralizado.
            sx={tableContainerListagemSx(containerHeight, isLoading || rows.length >= rowsPerPage)}
          >
            {/* table-layout: fixed (com width, não minWidth) — cada coluna respeita
                exatamente a largura declarada no header, então o conteúdo nunca força a
                tabela a crescer (por isso todo texto variável usa `noWrap`, truncando com
                "…" em vez de esticar a célula).
                CORREÇÃO: table-layout:fixed NÃO cresce além da `width` declarada quando a
                soma das colunas é maior — ele ENCOLHE as colunas proporcionalmente pra
                caber (comprovado: em viewport de celular a tabela ficava com texto de uma
                coluna vazando por cima da vizinha, não com scroll). `minWidth` (em vez de
                `width: '100%'`) resolve: acima dele a tabela cresce pra caber a largura
                disponível (colunas mantêm a proporção declarada), abaixo dele ela para de
                encolher e o TableContainer (que já tem overflow:auto) assume o scroll
                horizontal. Valor = soma exata das larguras das colunas abaixo. */}
            <Table size="small" sx={{ width: '100%', minWidth: smDown ? 0 : 955, tableLayout: 'fixed' }}>
              {!listaCelular && (
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: smDown ? undefined : '280px' }}>Produto</TableCell>
                  {!smDown && <TableCell sx={{ width: '70px' }}>Cód.</TableCell>}
                  {!smDown && <TableCell sx={{ width: '50px' }}>Un</TableCell>}
                  <TableCell sx={{ width: smDown ? '56px' : '80px' }}>Qtd.</TableCell>
                  {!smDown && <TableCell sx={{ width: '90px' }}>Marca</TableCell>}
                  {!smDown && <TableCell sx={{ width: '95px' }}>Referência</TableCell>}
                  {!smDown && <TableCell sx={{ width: '80px' }}>Tipo</TableCell>}
                  {!smDown && <TableCell sx={{ width: '80px' }}>Local</TableCell>}
                  <TableCell align='right' sx={{ width: smDown ? '84px' : '90px' }}>Valor</TableCell>
                  <TableCell sx={{ width: smDown ? '32px' : '40px' }} />
                </TableRow>
              </TableHead>
              )}

              {isLoading && rows.length === 0 && (
                <TableBody>
                  <LinhasEsqueletoTabela
                    colunas={smDown ? [
                      { width: '85%' },
                      { width: '60%' },
                      { width: '65%', align: 'right' },
                      { variant: 'circular', width: 24, height: 24 },
                    ] : [
                      { width: '85%' },
                      { width: '60%' },
                      { width: '50%' },
                      { width: '60%' },
                      { variant: 'rounded', width: '70%', height: 22 },
                      { width: '70%' },
                      { variant: 'rounded', width: '60%', height: 22 },
                      { width: '70%' },
                      { width: '65%', align: 'right' },
                      { variant: 'circular', width: 24, height: 24 },
                    ]}
                  />
                </TableBody>
              )}
              <TableBody>
                {listaCelular ? (
              <CelulaListaMobile>
                <ListaMobile
                  linhas={rows}
                  chave={(row) => row.idproduto}
                  renderizar={(row) => (
                    <LinhaListagemMobile
                      avatar={row.foto
                        ? <Avatar variant="rounded" src={process.env.REACT_APP_URL_IMGPRODUTOS + row.foto} alt={row.descricao} sx={{ width: 38, height: 38, borderRadius: '12px' }} />
                        : row.descricao}
                      titulo={row.descricao}
                      detalhes={[row.codinterno, row.marca, row.referencia, row.localizado]}
                      valor={moedaMobile(row.pvenda)}
                      corValor="success.main"
                      etiqueta={{
                        rotulo: `${(row.quan ?? 0).toLocaleString('pt-BR')} ${row.un || ''}`.trim(),
                        cor: (row.quan ?? 0) < 0 ? 'error' : (row.quan ?? 0) === 0 ? 'warning' : 'default',
                      }}
                      onClick={() => navigate(`/produto/${row.idproduto}`, { state: { from: `${location.pathname}${location.search}` } })}
                    />
                  )}
                />
              </CelulaListaMobile>
            ) : rows.map((row, index) => {
                  const estoqueNegativo = (row.quan ?? 0) < 0;
                  const estoqueZerado = (row.quan ?? 0) === 0;
                  const irParaProduto = () => navigate(`/produto/${row.idproduto}`, { state: { from: `${location.pathname}${location.search}` } });
                  return (
                    <TableRow
                      key={row.idproduto}
                      ref={linhaRefCallback(index)}
                      hover
                      tabIndex={0}
                      sx={{
                        cursor: 'pointer',
                        outline: 'none',
                        '&:last-child td': { borderBottom: 0 },
                        '& td': {
                          py: smDown ? 0.75 : 1.25,
                          px: smDown ? 0.75 : 1.5,
                          fontSize: smDown ? '0.75rem' : '0.8125rem',
                          borderBottom: `1px solid ${theme.torq.border.default}`,
                        },
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.035),
                        },
                        // Indicador visual de qual linha está focada — necessário porque a
                        // navegação por teclado (setas) move o foco entre as linhas, e sem
                        // isso o usuário não teria como ver em qual produto está.
                        '&:focus-visible': {
                          outline: `2px solid ${theme.torq.border.focus}`,
                          outlineOffset: '-2px',
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        },
                      }}
                      onClick={irParaProduto}
                      onKeyDown={onLinhaKeyDown(index)}
                    >
                      {/* Produto: imagem + descrição unificados numa coluna só (antes eram
                          colunas separadas — Img/EAN/Descrição — o que deixava a tabela com
                          aparência de planilha cheia de colunas). O EAN não aparece mais aqui. */}
                      <TableCell>
                        <Box display='flex' alignItems='center' gap={1.5}>
                          <Avatar
                            variant='rounded'
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '8px',
                              bgcolor: theme.palette.mode === 'dark'
                                ? alpha(theme.palette.text.secondary, 0.15)
                                : theme.palette.grey[100],
                              color: 'text.disabled',
                              flexShrink: 0,
                            }}
                            alt={row.descricao}
                            src={row.foto ? process.env.REACT_APP_URL_IMGPRODUTOS + row.foto : undefined}
                          >
                            <ImageRoundedIcon sx={{ fontSize: '16px' }} />
                          </Avatar>
                          <Typography
                            noWrap
                            sx={{ fontSize: '0.8125rem', fontWeight: 600, color: theme.torq.text.primary, minWidth: 0 }}
                          >
                            {row.descricao}
                          </Typography>
                        </Box>
                      </TableCell>

                      {!smDown && (
                        <TableCell>
                          <Typography noWrap sx={codigoInternoSx(theme)}>
                            {row.codinterno || '—'}
                          </Typography>
                        </TableCell>
                      )}
                      {!smDown && (
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                            {row.un}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '0.8125rem',
                            fontWeight: 600,
                            color: estoqueNegativo ? 'error.main' : estoqueZerado ? theme.torq.text.secondary : 'success.main',
                          }}
                        >
                          {(row.quan ?? 0).toLocaleString('pt-BR')}
                        </Typography>
                      </TableCell>
                      {/* Chip só quando existe marca de verdade — sem marca, mesmo traço neutro
                          das outras colunas vazias (Cód./Referência/Local) em vez de um chip
                          "Sem Marca" (peso visual desnecessário pra uma info ausente). Cor
                          sempre neutra (não mais o azul claro que tinha antes, que destacava
                          demais uma informação secundária). */}
                      {!smDown && (
                        <TableCell sx={{ overflow: 'hidden' }}>
                          {row.marca ? (
                            <Chip
                              label={row.marca}
                              size="small"
                              sx={{
                                maxWidth: '100%',
                                height: '22px',
                                fontSize: '0.6875rem',
                                fontWeight: 500,
                                '& .MuiChip-label': { px: 1.25, overflow: 'hidden', textOverflow: 'ellipsis' },
                                bgcolor: theme.torq.surface.level2,
                                color: theme.torq.text.secondary,
                              }}
                            />
                          ) : (
                            <Typography sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>—</Typography>
                          )}
                        </TableCell>
                      )}
                      {!smDown && (
                        <TableCell>
                          <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.primary }}>
                            {row.referencia || '—'}
                          </Typography>
                        </TableCell>
                      )}
                      {/* "Própria" (não "Original") pro caso true: `row.original` indica que
                          essa é a referência que corresponde ao cadastro, não necessariamente
                          que a peça é original de fábrica — "Original" prometia uma garantia
                          que o dado não confirma. "Similar" pro caso false continua correto. */}
                      {!smDown && (
                        <TableCell>
                          <Chip
                            label={row.original ? 'Própria' : 'Similar'}
                            size="small"
                            sx={{
                              height: '22px',
                              fontSize: '0.6875rem',
                              fontWeight: 600,
                              '& .MuiChip-label': { px: 1.25 },
                              bgcolor: row.original ? alpha(theme.palette.success.main, 0.12) : alpha(theme.palette.warning.main, 0.14),
                              color: row.original ? 'success.main' : 'warning.main',
                            }}
                          />
                        </TableCell>
                      )}
                      {!smDown && (
                        <TableCell>
                          <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.primary }}>
                            {row.localizado || '—'}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align='right'>
                        <Typography sx={valorMonetarioSx}>
                          {(row.pvenda ?? 0).toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL'
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell onClick={(event) => event.stopPropagation()}>
                        <IconButton
                          size='small'
                          onClick={irParaProduto}
                          sx={{
                            width: 32,
                            height: 32,
                            '&:hover': { backgroundColor: theme.torq.hover },
                          }}
                        >
                          <MoreVertRoundedIcon fontSize='small' />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              {rows.length === 0 && !isLoading && (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={smDown ? 4 : 10} align="center" sx={{ py: 3 }}>
                      <Box>
                        <ImageRoundedIcon sx={{
                          fontSize: 35,
                          color: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.text.secondary, 0.4)
                            : 'text.secondary',
                          mb: 1
                        }} />
                        <Typography variant='h6' color='text.secondary' gutterBottom fontSize="0.85rem">
                          {Environment.LISTAGEM_VAZIA}
                        </Typography>
                        <Typography variant='body2' color='text.secondary' fontSize="0.7rem">
                          {filter ? 'Tente ajustar os filtros de busca' : 'Nenhum produto encontrado'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}

              <TableFooter>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={smDown ? 4 : 10} sx={{ p: 0 }}>
                      <LinearProgress sx={{ height: '1.5px' }} />
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell colSpan={smDown ? 4 : 10} sx={{ py: 1.25, px: 2, borderTop: `1px solid ${theme.torq.border.default}` }}>
                    <Box
                      display='flex'
                      alignItems='center'
                      justifyContent='space-between'
                      flexWrap='wrap'
                      gap={1.5}
                    >
                      <Typography variant='caption' color='text.secondary'>
                        {totalCount === 0
                          ? 'Nenhum produto encontrado'
                          : `Mostrando de ${primeiroItem} a ${ultimoItem} de ${totalCount} produto${totalCount !== 1 ? 's' : ''}`}
                      </Typography>

                      {totalCount > rowsPerPage && (
                        <Pagination
                          page={pagina}
                          count={totalPaginas}
                          onChange={(_, newPage) => setSearchParams({
                            filter,
                            tipo,
                            modobusca,
                            pagina: newPage.toString()
                          }, { replace: true })}
                          size="small"
                          shape="rounded"
                          showFirstButton
                          showLastButton
                          siblingCount={1}
                          boundaryCount={1}
                          sx={{
                            // Página atual no mesmo navy fixo do botão "Novo
                            // Produto" (ver comentário lá em cima — não pode
                            // ser `theme.torq.sidebar.bg`, que fica branco no
                            // tema "Claro (menu claro)").
                            '& .MuiPaginationItem-root.Mui-selected': {
                              backgroundColor: brand.menuBase,
                              color: '#fff',
                              '&:hover': { backgroundColor: '#012D3D' },
                            },
                          }}
                        />
                      )}

                      <FormControl size='small' sx={{ minWidth: 110 }}>
                        <Select<number>
                          value={rowsPerPage}
                          onChange={handleChangeRowsPerPage}
                        >
                          {(OPCOES_POR_PAGINA.includes(rowsPerPage) ? OPCOES_POR_PAGINA : [...OPCOES_POR_PAGINA, rowsPerPage].sort((a, b) => a - b)).map((opcao) => (
                            <MenuItem key={opcao} value={opcao}>{opcao} / página</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Paper>
      </LayoutBaseDePagina>
    </>
  );
};
